import { useState, useMemo } from 'react';
import { Person } from '../types';

/**
 * usePeopleFilters Hook
 *
 * Custom hook for managing people filtering state and logic
 *
 * Features:
 * - Filter state management
 * - Client-side filtering logic
 * - Active filter count calculation
 * - Filter reset functionality
 *
 * @param {Person[]} persons - Array of all persons to filter
 * @returns {Object} Filter state and methods
 */
export interface PeopleFilters {
  searchQuery?: string;
  gender?: 'male' | 'female' | 'other';
  isDeceased?: boolean;
  birthYearFrom?: number;
  birthYearTo?: number;
  hasPhoto?: boolean;
  hasBiography?: boolean;
  lastName?: string;
}

interface UsePeopleFiltersReturn {
  filters: PeopleFilters;
  setFilters: React.Dispatch<React.SetStateAction<PeopleFilters>>;
  filteredPersons: Person[];
  activeFilterCount: number;
}

export const usePeopleFilters = (persons: Person[]): UsePeopleFiltersReturn => {
  // ===== State =====
  const [filters, setFilters] = useState<PeopleFilters>({});

  // ===== Computed Values =====
  const filteredPersons = useMemo(() => {
    let result = [...persons];

    // Filter by search query
    if (filters.searchQuery && filters.searchQuery.trim().length > 0) {
      const query = filters.searchQuery.toLowerCase().trim();
      result = result.filter((person) => {
        const fullName = `${person.firstName} ${person.middleName || ''} ${person.lastName}`.toLowerCase();
        const maidenName = person.maidenName?.toLowerCase() || '';
        return fullName.includes(query) || maidenName.includes(query);
      });
    }

    // Filter by gender
    if (filters.gender) {
      result = result.filter((person) => {
        return person.gender === filters.gender;
      });
    }

    // Filter by living status
    if (filters.isDeceased !== undefined) {
      result = result.filter((person) => person.isDeceased === filters.isDeceased);
    }

    // Filter by birth year range
    if (filters.birthYearFrom !== undefined || filters.birthYearTo !== undefined) {
      result = result.filter((person) => {
        if (!person.birthDate) return false;

        const birthYear = new Date(person.birthDate).getFullYear();

        if (filters.birthYearFrom !== undefined && birthYear < filters.birthYearFrom) {
          return false;
        }

        if (filters.birthYearTo !== undefined && birthYear > filters.birthYearTo) {
          return false;
        }

        return true;
      });
    }

    // Filter by has photo
    if (filters.hasPhoto !== undefined) {
      result = result.filter((person) => {
        const hasPhoto = !!(person.primaryPhotoId || (person.photoIds && person.photoIds.length > 0));
        return hasPhoto === filters.hasPhoto;
      });
    }

    // Filter by has biography
    if (filters.hasBiography !== undefined) {
      result = result.filter((person) => {
        const hasBio = !!(person.biography && person.biography.trim().length > 0);
        return hasBio === filters.hasBiography;
      });
    }

    // Filter by last name
    if (filters.lastName) {
      result = result.filter((person) => {
        return person.lastName.toLowerCase() === filters.lastName!.toLowerCase();
      });
    }

    return result;
  }, [persons, filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;

    if (filters.searchQuery && filters.searchQuery.trim().length > 0) count++;
    if (filters.gender) count++;
    if (filters.isDeceased !== undefined) count++;
    if (filters.birthYearFrom !== undefined || filters.birthYearTo !== undefined) count++;
    if (filters.hasPhoto !== undefined) count++;
    if (filters.hasBiography !== undefined) count++;
    if (filters.lastName) count++;

    return count;
  }, [filters]);

  // ===== Return =====
  return {
    filters,
    setFilters,
    filteredPersons,
    activeFilterCount,
  };
};

export default usePeopleFilters;
